// Full-screen defect detail view with consistent styling
// FT-06d: Added role-gated closure (skipper+ only can close, crew can only resolve)
// FT-209: Added "Generate Corrective Job" button for defect↔job linkage
// FT-353b: Hoisted "Generate Corrective Job" to detail header + dropped !isResolved gate
//          so Resolved defects (WBC3 fix-precedes-close-out) can still spawn a job.
import React, { useState, useMemo } from 'react';
import {
  Edit2, Trash2, CheckCircle, Clock, AlertTriangle,
  Calendar, User, MapPin, Settings, FileText, DollarSign, Wrench,
  ChevronDown, ChevronUp, Lock, ShieldCheck, Camera
} from 'lucide-react';
// FT-356c: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header raw ArrowLeft back button removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
import {
  FormField,
  FormGrid,
  Button,
  Input,
  Select,
  Textarea
} from '../../shared/FormComponents';
import { Badge } from '../../../catalyst/badge';
import { useSwipeBack, SwipeIndicator } from '../../../../hooks/useSwipeBack';
import { useVessel } from '../../../../contexts/VesselContext';
import { getContext } from '../../../../lib/context';
import { formatDateSafe, formatDateTimeSafe } from '../../../../utils/dateUtils';
import Dropdown from '../../../common/Dropdown';
import { usePermissions } from '../../../../hooks/usePermissions';
import { formatUserIdentity } from '../../../../utils/userDisplay';
import AddCustomJobModal from '../maintenance/AddCustomJobModal';
// FT-347: read-only photo grid on the defect detail view. We reuse
// <PhotoAttachment disabled> so the same thumb-grid renders — the disabled
// flag hides the capture button and the delete affordance (both grid + viewer).
import PhotoAttachment from '../../../common/PhotoAttachment';
// FT-353f: warning modal shown when closing a defect with an OPEN linked job.
import CloseWithOpenJobWarning from './CloseWithOpenJobWarning';
// FT-365 v3.1 hotfix: self-healing raw reader unwraps legacy double-JSON
// encoding left over from a brief FT-365 v3 exposure.
import { readCurrentVesselIdRaw } from '../../../../utils/readCurrentVesselId';

const DefectDetailView = ({
  defect,
  onBack,
  onEdit,
  onDelete,
  onStatusChange,
  crewList = [],
  // FT-209: Props for corrective job creation
  onGenerateCorrectiveJob,
  onNavigateToJob,
  maintenanceJobs
}) => {
  // Add swipe-to-go-back functionality
  const { containerRef, swipeProgress } = useSwipeBack(onBack);
  // FT-356c: register parent's onBack as the shell-level back handler.
  useSetBackHandler(onBack);
  // FT-347: vesselId for the read-only photo grid (thumbs pulled from IDB
  // via <PhotoAttachment>'s internal usePhotoStore).
  const { currentVesselId: contextVesselId } = useVessel();
  const currentVesselId = contextVesselId || readCurrentVesselIdRaw();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closingRemarks, setClosingRemarks] = useState('');
  const [closedBy, setClosedBy] = useState('');
  // FT-209: State for corrective job modal
  const [showGenerateJobModal, setShowGenerateJobModal] = useState(false);
  // FT-353f: State for "linked job still open" warning modal
  const [showOpenJobWarning, setShowOpenJobWarning] = useState(false);

  // New closeout form state (COMPLIANCE FIX)
  const [rootCause, setRootCause] = useState('');
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [verificationMethod, setVerificationMethod] = useState('');
  const [partsUsed, setPartsUsed] = useState('');
  const [additionalRemarks, setAdditionalRemarks] = useState('');

  // Audit trail expansion state
  const [auditExpanded, setAuditExpanded] = useState(true);

  // FT-209-6: Normalize correctiveJobId (may be string or object from legacy data)
  const correctiveJobId = typeof defect.correctiveJobId === 'string' ? defect.correctiveJobId : defect.correctiveJobId?.id || null;

  // FT-353f: Look up the linked corrective job so we can warn the user
  // if they try to close the defect while the job is still open.
  // "Open" = job exists in the active list AND has no completions yet.
  // Archived jobs are filtered out of `maintenanceJobs` by the domain
  // hook, so their absence here == treated as resolved.
  const linkedJob = useMemo(() => {
    if (!correctiveJobId || !Array.isArray(maintenanceJobs)) return null;
    return maintenanceJobs.find(j => j.id === correctiveJobId) || null;
  }, [correctiveJobId, maintenanceJobs]);
  const linkedJobStillOpen = useMemo(() => {
    if (!linkedJob) return false;
    const status = (linkedJob.status || 'active').toLowerCase();
    if (status === 'archived') return false;
    const completions = Array.isArray(linkedJob.completions) ? linkedJob.completions : [];
    return completions.length === 0;
  }, [linkedJob]);

  // FT-06d: Role-based permissions for defect closure
  const { isSkipper, isFleet, isSuperAdmin, currentUser } = usePermissions();
  const canClose = isSkipper || isFleet || isSuperAdmin; // Only skipper+ can close defects

  const isRepair = defect.record_type === 'repair';
  const isClosed = defect.status === 'Closed';
  const isResolved = defect.status === 'Resolved';
  const canEdit = !isClosed || isRepair; // FT-06d: Closed defects are locked (except repairs)

  // Dropdown options for Close Defect modal
  const crewOptions = useMemo(() => {
    if (!crewList || crewList.length === 0) return [];
    return crewList.map(member => ({
      value: member.name || `${member.firstName} ${member.lastName}`,
      label: member.name || `${member.firstName} ${member.lastName}`
    }));
  }, [crewList]);

  const rootCauseOptions = [
    { value: 'Wear and tear', label: 'Wear and tear' },
    { value: 'Manufacturing defect', label: 'Manufacturing defect' },
    { value: 'Improper use', label: 'Improper use' },
    { value: 'Environmental damage', label: 'Environmental damage' },
    { value: 'Lack of maintenance', label: 'Lack of maintenance' },
    { value: 'Installation error', label: 'Installation error' },
    { value: 'Unknown', label: 'Unknown' },
    { value: 'Other', label: 'Other (specify in remarks)' }
  ];

  const handleStatusChange = (newStatus) => {
    if (newStatus === 'Closed') {
      // FT-353f: intercept close attempts when the linked corrective job
      // is still open. Warning modal offers Close anyway / Cancel; the
      // close-anyway path re-enters this handler with the warning bypassed.
      if (linkedJobStillOpen) {
        setShowOpenJobWarning(true);
        return;
      }
      setShowCloseModal(true);
    } else if (onStatusChange) {
      // Log status change to history array (COMPLIANCE FIX)
      const updatedDefect = {
        ...defect,
        status: newStatus,
        updated_at: new Date().toISOString(),
        history: [
          ...(defect.history || []),
          {
            date: new Date().toISOString(),
            action: `Status changed to ${newStatus}`,
            by: getContext().userName || 'Unknown User',
            notes: `Status updated from ${defect.status} to ${newStatus}`
          }
        ]
      };
      onStatusChange(updatedDefect);
    }
  };

  const handleCloseDefect = () => {
    if (onStatusChange) {
      const updatedDefect = {
        ...defect,
        status: 'Closed',

        // New structured closeout fields (COMPLIANCE FIX)
        root_cause: rootCause,
        corrective_action: correctiveAction,
        verification_method: verificationMethod,
        parts_used: partsUsed || null,
        closing_remarks: additionalRemarks || null,

        // Existing fields
        closed_by: closedBy,
        date_closed: new Date().toISOString(),
        updated_at: new Date().toISOString(),

        // Append closeout to history (COMPLIANCE FIX)
        history: [
          ...(defect.history || []),
          {
            date: new Date().toISOString(),
            action: 'Defect closed',
            by: closedBy,
            notes: `Root cause: ${rootCause}. Corrective action: ${correctiveAction}`
          }
        ]
      };
      onStatusChange(updatedDefect);
    }

    // Reset ALL state (including new fields)
    setShowCloseModal(false);
    setClosedBy('');
    setClosingRemarks('');
    setRootCause('');
    setCorrectiveAction('');
    setVerificationMethod('');
    setPartsUsed('');
    setAdditionalRemarks('');
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(defect);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'text-red-600 bg-red-50';
      case 'High': return 'text-orange-600 bg-orange-50';
      case 'Medium': return 'text-yellow-600 bg-yellow-50';
      case 'Low': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'text-red-600 bg-red-50';
      case 'In Progress': return 'text-yellow-600 bg-yellow-50';
      case 'Resolved': return 'text-blue-600 bg-blue-50'; // FT-06d: New status - awaiting skipper closure
      case 'Closed': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  // Format timestamp for audit trail
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return formatDateTimeSafe(timestamp, 'Unknown');
  };

  return (
    <div className="min-h-screen bg-gray-50" ref={containerRef}>
      {/* Swipe Indicator */}
      <SwipeIndicator progress={swipeProgress} />
      
      {/* Header
          FT-353h: Responsive layout — on mobile (<md) the title block and the
          action-button cluster stack vertically so the "Generate Corrective
          Job" CTA + Edit/Delete buttons stay in-viewport (parent
          TailwindShell has overflow-x-hidden which was clipping the row on
          390px). Title-line pills wrap when they run out of horizontal room.
          Buttons themselves flex-wrap so they never overflow the container. */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 py-4 sm:px-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            {/* FT-356c: header raw ArrowLeft back button removed — bottom bar owns Back. */}
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                    {isRepair ? 'Repair' : 'Defect'} #{defect.id}
                  </h1>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(defect.status)}`}>
                    {defect.status}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(defect.risk_level)}`}>
                    {defect.risk_level} Priority
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Reported on {formatDateSafe(defect.date_reported)} by {defect.reported_by}
                </p>
              </div>
            </div>
            {/* Action cluster — wraps to next line on narrow viewports, primary
                CTA sits first so it stays visible even if Edit/Delete wrap. */}
            <div className="flex flex-wrap items-center gap-2 md:flex-nowrap md:justify-end">
              {/* FT-353b: Hoisted Generate Corrective Job CTA. Visible on Open /
                  In Progress / Resolved (fix-precedes-close-out per WBC3 8.3.4);
                  hidden once the defect is Closed OR already has a linked job.
                  Only defects (not repairs) can spawn a corrective job. */}
              {!isRepair && !isClosed && !correctiveJobId && (
                <Button
                  variant="primary"
                  onClick={() => setShowGenerateJobModal(true)}
                  data-testid="defect-header-generate-job"
                >
                  <Wrench className="h-4 w-4 mr-2" />
                  Generate Corrective Job
                </Button>
              )}
              {/* FT-353b: Already-linked case — replace the primary CTA with a
                  compact "view job" chip. Deep-link routing is FT-353d/e's job;
                  for now we reuse the existing onNavigateToJob handler which
                  the parent already wires to the maintenance module. */}
              {!isRepair && correctiveJobId && (
                <button
                  type="button"
                  onClick={() => onNavigateToJob?.(correctiveJobId)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-cyan-50 text-cyan-700 border border-cyan-200 hover:bg-cyan-100 transition-colors"
                  title={`View corrective job ${correctiveJobId}`}
                  data-testid="defect-header-view-job-chip"
                >
                  <Wrench className="h-3 w-3" aria-hidden="true" />
                  View job JOB-{String(correctiveJobId).slice(-5)}
                </button>
              )}
              {canEdit && (
                <Button variant="secondary" onClick={onEdit}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-6">
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white">Basic Information</h2>
            </div>
            <div className="px-6 py-5">
              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">DESCRIPTION</label>
                  <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                    <p className="text-sm text-gray-900">{defect.description}</p>
                  </div>
                </div>

                <FormGrid columns={2}>
                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      LOCATION
                    </label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-sm text-gray-900">{defect.location || 'Not specified'}</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 flex items-center gap-1">
                      <Settings className="h-3 w-3" />
                      EQUIPMENT LINKED
                    </label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-sm text-gray-900">{defect.equipment_linked || 'Not specified'}</p>
                    </div>
                  </div>
                </FormGrid>

                <FormGrid columns={3}>
                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      DATE {isRepair ? 'COMPLETED' : 'REPORTED'}
                    </label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-sm text-gray-900">{formatDateSafe(defect.date_reported)}</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {isRepair ? 'COMPLETED BY' : 'REPORTED BY'}
                    </label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-sm text-gray-900">{defect.reported_by}</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      ASSIGNED TO
                    </label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-sm text-gray-900">{defect.assigned_to || 'Unassigned'}</p>
                    </div>
                  </div>
                </FormGrid>
              </div>
            </div>
          </div>

          {/* FT-347: Evidence Photos — read-only grid.
              Uses <PhotoAttachment disabled> so capture button + delete affordance
              are hidden (see PhotoThumbnailGrid line 152 + PhotoViewer line 265).
              Section renders only when at least one photo exists on the record. */}
          {currentVesselId && Array.isArray(defect.photos) && defect.photos.length > 0 && (
            <div
              className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden"
              data-testid="defect-photos-readonly-section"
            >
              <div className="bg-slate-700 px-5 py-3">
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-white" aria-hidden="true" />
                  <h2 className="text-base font-semibold text-white">Evidence Photos</h2>
                </div>
              </div>
              <div className="px-6 py-5">
                <PhotoAttachment
                  recordType="defect"
                  recordId={defect.id}
                  vesselId={currentVesselId}
                  photos={defect.photos}
                  onChange={() => { /* read-only */ }}
                  disabled={true}
                  caption={true}
                  testId="defect-photos-readonly"
                />
              </div>
            </div>
          )}

          {/* Status & Priority / Repair Details */}
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white">
                {isRepair ? 'Repair Details' : 'Status & Priority'}
              </h2>
            </div>
            <div className="px-6 py-5">
              {!isRepair ? (
                <div className="space-y-4">
                  <FormGrid columns={3}>
                    <div>
                      <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">PRIORITY</label>
                      <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(defect.risk_level)}`}>
                        {defect.risk_level}
                      </span>
                    </div>

                    <div>
                      <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">STATUS</label>
                      <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(defect.status)}`}>
                        {defect.status}
                      </span>
                    </div>

                    {defect.date_closed && (
                      <div>
                        <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">DATE CLOSED</label>
                        <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                          <p className="text-sm text-gray-900">{formatDateSafe(defect.date_closed)}</p>
                        </div>
                      </div>
                    )}
                  </FormGrid>

                  {/* FT-06d: Role-gated status changes */}
                  {/* Closed defects show locked indicator */}
                  {/* FT-06e: Show "Name (Role)" format */}
                  {isClosed && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                      <Lock className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">
                        Defect closed by {formatUserIdentity(defect.closedByName || defect.closed_by, defect.closedByRole)} on {formatDateSafe(defect.closedAt || defect.date_closed)}
                      </span>
                    </div>
                  )}

                  {/* Open/In Progress defects: any user can resolve or progress */}
                  {!isClosed && !isResolved && (
                    <div>
                      <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-2 block">CHANGE STATUS</label>
                      <div className="flex flex-wrap gap-2">
                        {defect.status !== 'In Progress' && (
                          <Button
                            variant="secondary"
                            onClick={() => handleStatusChange('In Progress')}
                          >
                            <Clock className="h-4 w-4 mr-2" />
                            Mark In Progress
                          </Button>
                        )}

                        {/* FT-353b: Generate Corrective Job button hoisted to
                            the detail header; the "View Job" indicator moved to
                            a chip in the same header row. This CHANGE STATUS
                            group now only carries status-transition actions. */}

                        {/* Any user can mark as Resolved */}
                        <Button
                          variant="secondary"
                          onClick={() => handleStatusChange('Resolved')}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Mark as Resolved
                        </Button>
                        {/* Only skipper+ can directly close */}
                        {canClose && (
                          <Button
                            variant="primary"
                            onClick={() => handleStatusChange('Closed')}
                          >
                            <ShieldCheck className="h-4 w-4 mr-2" />
                            Close Defect
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Resolved defects: skipper+ can close, others see pending message */}
                  {isResolved && !isClosed && (
                    <div>
                      <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-2 block">AWAITING CLOSURE</label>
                      {canClose ? (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="primary"
                            onClick={() => handleStatusChange('Closed')}
                          >
                            <ShieldCheck className="h-4 w-4 mr-2" />
                            Close Defect
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={() => handleStatusChange('Open')}
                          >
                            Reopen Defect
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                          <Clock className="h-4 w-4 text-blue-600" />
                          <span className="text-sm text-blue-700">
                            Resolved by {defect.resolvedByName || 'crew'} — awaiting skipper/manager sign-off to close
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <FormGrid columns={2}>
                    <div>
                      <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">PARTS USED</label>
                      <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                        <p className="text-sm text-gray-900">{defect.parts_used || 'Not specified'}</p>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        REPAIR COST
                      </label>
                      <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                        <p className="text-sm text-gray-900">{defect.repair_cost || 'Not specified'}</p>
                      </div>
                    </div>
                  </FormGrid>

                  <FormGrid columns={2}>
                    <div>
                      <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">CONTRACTOR DETAILS</label>
                      <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                        <p className="text-sm text-gray-900">{defect.contractor_details || 'Not specified'}</p>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">REPAIR METHOD</label>
                      <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                        <p className="text-sm text-gray-900">{defect.repair_method || 'Not specified'}</p>
                      </div>
                    </div>
                  </FormGrid>
                </div>
              )}
            </div>
          </div>

          {/* Actions Taken */}
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white">Actions & Notes</h2>
            </div>
            <div className="px-6 py-5">
              <div className="space-y-4">
                {defect.immediate_action_taken && (
                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">
                      {isRepair ? 'WORK COMPLETED' : 'IMMEDIATE ACTION TAKEN'}
                    </label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{defect.immediate_action_taken}</p>
                    </div>
                  </div>
                )}

                {defect.corrective_action_required && !isRepair && (
                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">
                      CORRECTIVE ACTION REQUIRED
                    </label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{defect.corrective_action_required}</p>
                    </div>
                  </div>
                )}

                {defect.fixed_immediately && (
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-sm text-green-800">
                      <CheckCircle className="h-4 w-4 inline mr-1" />
                      This defect was fixed immediately upon discovery
                    </p>
                  </div>
                )}

                {/* NEW: Display structured closeout fields (COMPLIANCE FIX) */}
                {defect.root_cause && (
                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">ROOT CAUSE</label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-sm text-gray-900">{defect.root_cause}</p>
                    </div>
                  </div>
                )}

                {defect.corrective_action && (
                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">CORRECTIVE ACTION TAKEN</label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{defect.corrective_action}</p>
                    </div>
                  </div>
                )}

                {defect.verification_method && (
                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">VERIFICATION METHOD</label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{defect.verification_method}</p>
                    </div>
                  </div>
                )}

                {defect.parts_used && !isRepair && (
                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">PARTS USED</label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{defect.parts_used}</p>
                    </div>
                  </div>
                )}

                {defect.closing_remarks && (
                  <div className="mt-4">
                    <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">
                      ADDITIONAL REMARKS
                    </label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{defect.closing_remarks}</p>
                    </div>
                    {/* FT-06e: Show "Name (Role)" format */}
                    {defect.closed_by && (
                      <p className="text-xs text-gray-500 mt-2">
                        Closed by: {formatUserIdentity(defect.closedByName || defect.closed_by, defect.closedByRole)} on {formatDateSafe(defect.date_closed, 'Unknown date')}
                      </p>
                    )}
                  </div>
                )}

                {/* General Notes - Display notes field from creation form */}
                {defect.notes && (
                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1 block">
                      NOTES
                    </label>
                    <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{defect.notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Status History - NEW SECTION (COMPLIANCE FIX) */}
          {defect.history && defect.history.length > 0 && (
            <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
              <div className="bg-slate-700 px-5 py-3">
                <h2 className="text-base font-semibold text-white">Status History</h2>
              </div>
              <div className="px-6 py-5">
                <div className="space-y-3">
                  {defect.history.slice().reverse().map((entry, idx) => (
                    <div key={idx} className="flex gap-4 pb-3 border-b border-gray-100 last:border-0 last:pb-0">
                      <div className="flex-shrink-0 w-32">
                        <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">
                          {formatDateSafe(entry.date, '-', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatDateSafe(entry.date, '-', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">{entry.action}</p>
                        <p className="text-sm text-gray-600 mt-0.5">
                          <User className="h-3 w-3 inline mr-1" />
                          {entry.by}
                        </p>
                        {entry.notes && (
                          <p className="text-xs text-gray-500 mt-2 pl-4 border-l-2 border-gray-200">
                            {entry.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Audit Trail Section - MCA Compliance */}
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <button
              onClick={() => setAuditExpanded(!auditExpanded)}
              className="w-full bg-slate-700 px-5 py-3 flex items-center justify-between hover:bg-slate-500 transition-colors"
            >
              <h2 className="text-base font-semibold text-white flex items-center">
                <FileText className="h-5 w-5 mr-2 text-white" />
                Audit Trail {defect.auditLog && defect.auditLog.length > 0 && `(${defect.auditLog.length} ${defect.auditLog.length === 1 ? 'entry' : 'entries'})`}
              </h2>
              {auditExpanded ? (
                <ChevronUp className="h-5 w-5 text-white" />
              ) : (
                <ChevronDown className="h-5 w-5 text-white" />
              )}
            </button>
            {auditExpanded && (
              <div className="px-6 py-4 space-y-3 text-sm max-h-96 overflow-y-auto">
                {defect.auditLog && defect.auditLog.length > 0 ? (
                  defect.auditLog.slice().reverse().map((entry, index) => (
                    <div key={entry.id || index} className="text-gray-700 pb-3 border-b border-gray-100 last:border-0 last:pb-0">
                      <div className="font-medium flex items-center gap-2">
                        <span className="text-cyan-600">•</span>
                        <span>
                          {entry.action === 'created' ? 'Created' : 'Edited'} by {entry.user}
                        </span>
                        <span className="text-gray-400">-</span>
                        <span className="text-gray-500 text-xs">{formatTimestamp(entry.timestamp)}</span>
                      </div>
                      <div className="ml-4 text-gray-600 space-y-1 mt-1">
                        {/* Summary */}
                        {entry.summary && entry.action !== 'created' && (
                          <div className="text-sm">{entry.summary}</div>
                        )}

                        {/* Field-level changes */}
                        {entry.changes && entry.changes.length > 0 && (
                          <div className="ml-2 text-xs space-y-0.5 bg-gray-50 rounded p-2 mt-1">
                            {entry.changes.map((change, changeIndex) => (
                              <div key={changeIndex} className="text-gray-500">
                                <span className="font-medium text-gray-600">{change.field.replace(/_/g, ' ')}:</span>
                                {' "'}
                                <span className="text-red-500 line-through">
                                  {String(change.displayOld || change.oldValue || 'empty')}
                                </span>
                                {'" → "'}
                                <span className="text-green-600">
                                  {String(change.displayNew || change.newValue)}
                                </span>
                                {'"'}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Reason */}
                        {entry.reason && entry.reason !== 'Initial record creation' && (
                          <div className="text-xs mt-1">
                            <span className="font-medium">Reason:</span> {entry.reason}
                          </div>
                        )}

                        {/* Vessel */}
                        {entry.vessel && (
                          <div className="text-xs text-gray-400">Vessel: {entry.vessel}</div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  // Fallback for defects without audit log (legacy records)
                  <div className="text-gray-700">
                    <div className="font-medium flex items-center gap-2">
                      <span className="text-cyan-600">•</span>
                      <span>Created by {defect.reported_by || 'Unknown user'}</span>
                      <span className="text-gray-400">-</span>
                      <span className="text-gray-500 text-xs">
                        {defect.createdAt ? formatTimestamp(defect.createdAt) :
                         (defect.date_reported ? formatTimestamp(defect.date_reported) : 'Unknown date')}
                      </span>
                    </div>
                    <div className="ml-4 text-gray-600 mt-1">
                      <div className="text-xs">Initial record creation (legacy record - created before audit system implemented)</div>
                      {defect.vesselName && (
                        <div className="text-xs text-gray-400 mt-1">Vessel: {defect.vesselName}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* WBC3 Compliance Note */}
          {defect.status === 'Closed' && !isRepair && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                <FileText className="h-4 w-4 inline mr-1" />
                WBC3 Compliance Note
              </h4>
              <p className="text-xs text-blue-700">
                This defect record is now closed and cannot be modified per WBC3 Section 8.1.4 & 8.3.4
                audit compliance requirements. The record will be retained for inspection purposes.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Delete {isRepair ? 'Repair' : 'Defect'} Record</h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete this record? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleDelete}>
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Close Defect Modal - Structured Closeout (COMPLIANCE FIX) */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Close Defect</h3>

              <div className="space-y-6">
                {/* Person & Root Cause Section */}
                <div className="rounded-2xl border border-gray-200 shadow-sm bg-white">
                  <div className="bg-slate-700 px-5 py-3">
                    <h2 className="text-base font-semibold text-white">Person & Root Cause</h2>
                  </div>

                  <div className="px-6 py-5 space-y-4">
                    {/* Closed By - REQUIRED */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Closed By <span className="text-red-500">*</span>
                      </label>
                      <Dropdown
                        value={closedBy}
                        onChange={(value) => setClosedBy(value)}
                        options={crewOptions}
                        placeholder="Select person..."
                        width="w-full"
                      />
                    </div>

                    {/* Root Cause - REQUIRED (COMPLIANCE) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Root Cause <span className="text-red-500">*</span>
                      </label>
                      <Dropdown
                        value={rootCause}
                        onChange={(value) => setRootCause(value)}
                        options={rootCauseOptions}
                        placeholder="Select root cause..."
                        width="w-full"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Required for WBC3 8.3.4 compliance - root cause analysis
                      </p>
                    </div>
                  </div>
                </div>

                {/* Corrective Actions Section */}
                <div className="rounded-2xl border border-gray-200 shadow-sm bg-white">
                  <div className="bg-slate-700 px-5 py-3">
                    <h2 className="text-base font-semibold text-white">Corrective Actions</h2>
                  </div>

                  <div className="px-6 py-5 space-y-4">
                    {/* Corrective Action Taken - REQUIRED (COMPLIANCE) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Corrective Action Taken <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={correctiveAction}
                        onChange={(e) => setCorrectiveAction(e.target.value)}
                        rows={3}
                        placeholder="Describe what was done to fix the defect..."
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Detailed description of repair/corrective action performed
                      </p>
                    </div>

                    {/* Verification Method - REQUIRED (COMPLIANCE) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Verification/Testing Method <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={verificationMethod}
                        onChange={(e) => setVerificationMethod(e.target.value)}
                        rows={2}
                        placeholder="How was the repair verified/tested..."
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        How you confirmed the defect is fixed (testing, inspection, etc.)
                      </p>
                    </div>
                  </div>
                </div>

                {/* Additional Details Section */}
                <div className="rounded-2xl border border-gray-200 shadow-sm bg-white">
                  <div className="bg-slate-700 px-5 py-3">
                    <h2 className="text-base font-semibold text-white">Additional Details</h2>
                  </div>

                  <div className="px-6 py-5 space-y-4">
                    {/* Parts Used - OPTIONAL */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Parts Used (Optional)
                      </label>
                      <textarea
                        value={partsUsed}
                        onChange={(e) => setPartsUsed(e.target.value)}
                        rows={2}
                        placeholder="Part numbers, supplier details, warranty info..."
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                      />
                    </div>

                    {/* Additional Remarks - OPTIONAL */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Remarks (Optional)
                      </label>
                      <textarea
                        value={additionalRemarks}
                        onChange={(e) => setAdditionalRemarks(e.target.value)}
                        rows={2}
                        placeholder="Any other relevant information..."
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowCloseModal(false);
                    setClosedBy('');
                    setRootCause('');
                    setCorrectiveAction('');
                    setVerificationMethod('');
                    setPartsUsed('');
                    setAdditionalRemarks('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCloseDefect}
                  disabled={!closedBy || !rootCause || !correctiveAction || !verificationMethod}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Close Defect
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FT-209: Generate Corrective Job Modal */}
      {showGenerateJobModal && (
        <AddCustomJobModal
          isOpen={showGenerateJobModal}
          onClose={() => setShowGenerateJobModal(false)}
          onSave={(newJob) => {
            // Call parent handler to save job and link to defect
            if (onGenerateCorrectiveJob) {
              onGenerateCorrectiveJob(newJob, defect);
            }
            setShowGenerateJobModal(false);
          }}
          linkedDefect={defect}
          existingJobs={maintenanceJobs}
        />
      )}

      {/* FT-353f: warning shown when closing a defect with an OPEN linked job */}
      {showOpenJobWarning && (
        <CloseWithOpenJobWarning
          job={linkedJob}
          onCancel={() => setShowOpenJobWarning(false)}
          onCloseAnyway={() => {
            setShowOpenJobWarning(false);
            setShowCloseModal(true);
          }}
        />
      )}
    </div>
  );
};

export default DefectDetailView;